import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { shouldUseDemoMode, demoAPI } from '../../utils/demoMode';
import { API_URL } from '../../lib/api-client';
import { setProfile } from './profileSlice';
import { logger } from '../../utils/logger';

const USE_DEMO_MODE = shouldUseDemoMode();

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: string;
  subscription?: any;
  isEmailVerified?: boolean;
  isPhoneVerified?: boolean;
  preferences?: any;
}

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  loading: false,
  error: null,
  isAuthenticated: false,
};

// Set axios default header if token exists
if (initialState.token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${initialState.token}`;
}

// Async thunks
export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue, dispatch }) => {
    try {
      logger.log('🔐 Redux: Attempting email registration for:', email);
      const { data } = await axios.post(`${API_URL}/auth/register`, { email, password });
      
      if (data.success) {
        logger.log('✅ Redux: Registration successful');
        localStorage.setItem('token', data.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.data.token}`;
        
        // Fetch profile data after successful registration and sync to profileSlice
        try {
          const profileRes = await axios.get(`${API_URL}/profiles/me`);
          if (profileRes.data.success) {
            logger.log('📋 Redux: Syncing profile data to profileSlice');
            dispatch(setProfile(profileRes.data.data)); // Sync to profileSlice
            return { 
              ...data.data, 
              profile: profileRes.data.data 
            };
          }
        } catch (_error) {
          logger.log('ℹ️ No profile found yet');
        }
        
        return data.data;
      }
      return rejectWithValue('Registration failed');
    } catch (_error: any) {
      console.error('❌ Redux: Registration error:', _error.response?.data?.message);
      return rejectWithValue(_error.response?.data?.message || 'Registration failed');
    }
  }
);

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue, dispatch }) => {
    try {
      logger.log('🔐 Redux: Attempting email login for:', email);
      const { data } = await axios.post(`${API_URL}/auth/login`, { email, password });
      
      if (data.success) {
        logger.log('✅ Redux: Login successful');
        localStorage.setItem('token', data.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.data.token}`;
        
        // Fetch profile data after successful login and sync to profileSlice
        try {
          const profileRes = await axios.get(`${API_URL}/profiles/me`);
          if (profileRes.data.success) {
            logger.log('📋 Redux: Syncing profile data to profileSlice');
            dispatch(setProfile(profileRes.data.data)); // Sync to profileSlice
            return { 
              ...data.data, 
              profile: profileRes.data.data 
            };
          }
        } catch (_error) {
          logger.log('ℹ️ No profile found yet');
        }
        
        return data.data;
      }
      return rejectWithValue('Login failed');
    } catch (_error: any) {
      console.error('❌ Redux: Login error:', _error.response?.data?.message);
      return rejectWithValue(_error.response?.data?.message || 'Login failed');
    }
  }
);

export const loadUser = createAsyncThunk(
  'auth/loadUser',
  async (_, { rejectWithValue, dispatch }) => {
    const token = localStorage.getItem('token');
    const demoMode = localStorage.getItem('demoMode');
    
    if (!token) {
      return rejectWithValue('No token found');
    }

    // Use demo mode if enabled
    if (USE_DEMO_MODE || demoMode === 'true') {
      try {
        logger.log('👤 Redux: Loading user from demo mode...');
        const result = await demoAPI.loadUser(token);
        
        if (result.success && result.data) {
          logger.log('✅ Redux: Demo user loaded successfully');
          return { user: result.data.user };
        }
        return rejectWithValue('Failed to load demo user');
      } catch (_error: any) {
        // Silently fail in demo mode - don't log errors
        localStorage.removeItem('token');
        localStorage.removeItem('demoMode');
        return rejectWithValue('Demo mode token invalid');
      }
    }

    try {
      logger.log('👤 Redux: Loading user from backend...');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const { data } = await axios.get(`${API_URL}/auth/me`);
      
      if (data.success) {
        logger.log('✅ Redux: User loaded successfully:', data.data.user.email);
        
        // Fetch profile data after loading user and sync to profileSlice
        try {
          const profileRes = await axios.get(`${API_URL}/profiles/me`);
          if (profileRes.data.success) {
            logger.log('📋 Redux: Syncing profile data to profileSlice');
            dispatch(setProfile(profileRes.data.data)); // Sync to profileSlice
            return { 
              user: data.data.user, 
              profile: profileRes.data.data 
            };
          }
        } catch (_error) {
          logger.log('ℹ️ No profile found yet');
        }
        
        return { user: data.data.user };
      }
      return rejectWithValue('Failed to load user');
    } catch (_error: any) {
      // Silently handle backend connection errors
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      return rejectWithValue('Backend not accessible');
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async (userData: Partial<User>, { rejectWithValue }) => {
    try {
      logger.log('👤 Redux: Updating user...', userData);
      const { data } = await axios.put(`${API_URL}/users/me`, userData);
      
      if (data.success) {
        logger.log('✅ Redux: User updated successfully');
        return data.data.user;
      }
      return rejectWithValue('Update failed');
    } catch (_error: any) {
      console.error('❌ Redux: Update user error:', _error.response?.data?.message);
      return rejectWithValue(_error.response?.data?.message || 'Update failed');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { dispatch }) => {
    logger.log('🚪 Redux: Logging out...');
    localStorage.removeItem('token');
    localStorage.removeItem('demoMode');
    delete axios.defaults.headers.common['Authorization'];
    
    // Clear profile slice on logout
    dispatch({ type: 'profile/clearProfile' });
    
    return null;
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Register user
    builder.addCase(registerUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(registerUser.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.error = null;
    });
    builder.addCase(registerUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Login user
    builder.addCase(loginUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.error = null;
    });
    builder.addCase(loginUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Load user
    builder.addCase(loadUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(loadUser.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload.user;
      state.isAuthenticated = true;
      state.error = null;
    });
    builder.addCase(loadUser.rejected, (state) => {
      state.loading = false;
      state.user = null;
      state.isAuthenticated = false;
    });

    // Logout user
    builder.addCase(logoutUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.loading = false;
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
    });
    builder.addCase(logoutUser.rejected, (state) => {
      state.loading = false;
    });

    // Update user profile
    builder.addCase(updateUserProfile.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateUserProfile.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload;
      state.error = null;
    });
    builder.addCase(updateUserProfile.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const { clearError, setUser } = authSlice.actions;
export default authSlice.reducer;